<template>
  <li class="collab-tile">
    <div class="collab-tile__left-side">
      <!-- User info -->
      <div class="user-info collab-tile__user-info">
        <div class="user-info__avatar-n-msg-count">
          <RouterLink class="user-info__msg-count-link-wrap" :to="props.to || props.toForMessageButton ? props.to || props.toForMessageButton : buildCollabLink(true)">
            <MsgCountBadge v-if="sharedSummaryFields.unreadMessageCount" class="user-info__msg-count" :count="sharedSummaryFields.unreadMessageCount" isHoverable />
          </RouterLink>
          <AvatarWithFallback class="user-info__avatar" :src="avatarUri" :isRounded="isCustomerCollab" :objectFit="isCustomerCollab ? 'cover' : 'contain'" />
        </div>

        <div class="user-info__name">
          {{ isCustomerCollab ? customerCollabActionSummary.creatorName : creatorCollabActionSummary.customerName }}
        </div>
      </div>
      <!-- / User info -->
    </div>

    <RouterLink class="collab-tile__right-side" :to="props.to ? props.to : buildCollabLink(false)">
      <!-- Custom collab badge -->
      <div class="custom-collab-badge collab-tile__custom-collab-badge">
        <img class="custom-collab-badge__icon" src="https://cdn.shrpa.com/images/shrpa-logo-border-white-150.png" alt="Shrpa" />
        <div class="custom-collab-badge__text">Creator Visit</div>
      </div>
      <!-- / Custom collab badge -->

      <SrpButton
        class="collab-tile__cta-btn"
        :size="screenSize === 'mobile' ? 'tiny' : 'small'"
        :fill="sharedSummaryFields.actionRequired ? 'solid' : 'outlined'"
        :color="sharedSummaryFields.actionRequired ? 'orange' : 'teal'"
      >
        {{ sharedSummaryFields.actionRequired ? sharedSummaryFields.statusDisplayText : "View Creator Visit" }}
        <template #iconRight>
          <IconEmbedded name="caret-right_3-5" :size="screenSize === 'mobile' ? 11 : 13" color="rgba(5, 133, 135, 0.1)" />
        </template>
      </SrpButton>

      <h4 class="global-h4 collab-tile__collab-name">
        {{ getCollabNameWithFallback(sharedSummaryFields.collabName) }}
      </h4>

      <div class="collab-tile__date-n-status">
        <div v-if="sharedSummaryFields.visitDate" class="collab-tile__date">
          {{ moment(sharedSummaryFields.visitDate).format("MMM Do YYYY") }}
        </div>
        <div v-else-if="sharedSummaryFields.preferredDateRangeStart && sharedSummaryFields.preferredDateRangeEnd" class="collab-tile__date">
          {{ DateUtils.formatDateRangeTwoDates(sharedSummaryFields.preferredDateRangeStart, sharedSummaryFields.preferredDateRangeEnd) }}
        </div>
        <CollabStatusStripe
          v-if="!sharedSummaryFields.actionRequired && (sharedSummaryFields.status || sharedSummaryFields.statusDisplayText)"
          class="collab-tile__status-stripe"
          :color="isCollabCompleted ? 'green' : 'white'"
          :isWithCheckIcon="isCollabCompleted"
          :text="isCollabCompleted ? 'Completed' : sharedSummaryFields.statusDisplayText"
        />
      </div>
    </RouterLink>
  </li>
</template>

<script setup lang="ts">
import { inject, Ref, onMounted, computed } from "vue";
import moment from "moment";
import DateUtils from "@logic/DateUtils";
import { getCollabNameWithFallback } from "@helpers/CollabHelper";

// Components
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import MsgCountBadge from "@components/MsgCountBadge.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import CollabStatusStripe from "@components/CollabStatusStripe.vue";

// Types
import { CreatorCollabActionSummary, CustomerCollabActionSummary, SharedCollabActionSummary } from "@contracts/collabActionSummaries";
import { ScreenSize } from "@contracts/screenSize";
import { RouteLocationRaw } from "vue-router";

// Global variables & injections
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;
const screenSize = inject("screenSize") as Ref<ScreenSize>;

const props = withDefaults(
  defineProps<{
    customerCollabActionSummary?: CustomerCollabActionSummary | null;
    creatorCollabActionSummary?: CreatorCollabActionSummary | null;
    to?: object | null;
    toForMessageButton?: object | null;
  }>(),
  {
    customerCollabActionSummary: null,
    creatorCollabActionSummary: null,
    to: null,
    toForMessageButton: null,
  }
);

// Is customer collab action summary ==========================================
const isCustomerCollab = computed<boolean>(() => Boolean(props.customerCollabActionSummary) && !props.creatorCollabActionSummary);

// Calc is Collab completed ==========================================================
const isCollabCompleted = computed<boolean>(() => sharedSummaryFields.value.completedDate !== null);

// Avatar URI =================================================================
const avatarUri = computed<string>(() => {
  return props.creatorCollabActionSummary?.customerLogo
    ? `${contentBaseUri}/cms/images/orgs/${props.creatorCollabActionSummary?.customerLogo}`
    : props.customerCollabActionSummary?.creatorProfileImage;
});

// Shared fields ==============================================================
const sharedSummaryFields = computed<SharedCollabActionSummary>(() => {
  return isCustomerCollab.value ? props.customerCollabActionSummary : props.creatorCollabActionSummary;
});

// Generate the collab router link ============================================
function buildCollabLink(openMessage = false): RouteLocationRaw {
  const messageParam = openMessage ? `messages=true` : "";
  return isCustomerCollab.value
    ? {
        name: "PaidCollabCommunityAdmin",
        params: {
          communityId: sharedSummaryFields.value.customerId,
        },
        hash: `#collab=${sharedSummaryFields.value.collabInputId}&${messageParam}`,
      }
    : {
        name: "CollabOpportunity",
        params: {
          communityId: sharedSummaryFields.value.customerId,
          collabInputId: sharedSummaryFields.value.collabInputId,
          creatorId: sharedSummaryFields.value.creatorId,
        },
        hash: `#${messageParam}`,
      };
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Custom collab badge ========================================================
.custom-collab-badge {
  height: 20px;
  padding: 0 11px 2px 9px;
  box-sizing: border-box;
  border: 1px rgb(193, 193, 193, 1) solid;
  border-radius: 100px;
  display: flex;
  align-items: center;
  background: rgba(231, 237, 240, 1);

  &__icon {
    width: 14px;
    margin-right: 1px;
  }

  &__text {
    color: rgba(0, 0, 0, 1);
    font:
      600 12px/12px "Quicksand",
      sans-serif;
  }
}

// User info ==================================================================
.user-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &__avatar-n-msg-count {
    margin-bottom: 3px;
    position: relative;
    z-index: 0;
  }

  &__avatar {
    width: 75px;
    position: relative;
    z-index: 0;
  }

  &__msg-count-link-wrap {
    position: absolute;
    inset: 0 -10px auto auto;
    z-index: 1;
  }

  &__msg-count {
  }

  &__name {
    max-width: 100%;
    text-align: center;
    text-wrap: wrap;
    white-space: wrap;
    word-break: break-word;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .user-info {
    &__avatar {
      width: 65px;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .user-info {
    &__avatar {
      width: 60px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .user-info {
    &__avatar {
      width: 50px;
    }
  }
}

// Collab tile ================================================================
.collab-tile {
  border: 1px rgba(0, 0, 0, 0.1) solid;
  border-radius: 4px;
  display: flex;
  align-items: stretch;
  color: rgba(91, 91, 91, 1);
  font: 14px/19px sans-serif;

  // left side ------------------------
  &__left-side {
    width: 120px;
    min-width: 120px;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__user-info {
    max-width: 100%;
  }

  // right side -----------------------
  &__right-side {
    flex-grow: 1;
    padding: 18px 18px 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    z-index: 0;
    color: rgba(91, 91, 91, 1);
    background: rgba(231, 237, 240, 1);
    cursor: pointer;
  }

  &__custom-collab-badge {
    position: absolute;
    inset: -10px auto auto 15px;
    pointer-events: none;
  }

  &__collab-name {
    margin-bottom: 5px;
  }

  &__right-side:hover &__collab-name {
    text-decoration: underline;
    text-decoration-color: rgba(0, 0, 0, 0.2);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
  }

  &__date-n-status {
    gap: 7px 10px;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__date {
  }

  &__status-stripe {
  }

  &__cta-btn {
    order: 100;
    border-radius: 4px;
    background: rgba(255, 255, 255, 1);
  }
  &__cta-btn:hover + &__collab-name {
    text-decoration: none;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .collab-tile {
    // left side ------------------------
    &__left-side {
      width: 100px;
      min-width: 100px;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .collab-tile {
    // left side ------------------------
    &__left-side {
      width: 90px;
      min-width: 90px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .collab-tile {
    // left side ------------------------
    &__left-side {
      width: 80px;
      min-width: 80px;
    }
  }
}
</style>
